import React, { useState, useEffect } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useApp } from '../../AppContext';
import SideHustleLayout from '../../components/SideHustleLayout';

interface StartupCostItem {
  item: string;
  amount: number;
}

interface BusinessCanvasData {
  problem: string;
  solution: string;
  competitiveAdvantage: string;
  marketCompetition: string;
  team: string;
  idealCustomer: string;
  businessModel: string;
  startupCosts: StartupCostItem[];
}

const BusinessCanvas: React.FC = () => {
  const { currentUser } = useApp();
  const [formData, setFormData] = useState<BusinessCanvasData>({
    problem: '',
    solution: '',
    competitiveAdvantage: '',
    marketCompetition: '',
    team: '',
    idealCustomer: '',
    businessModel: '',
    startupCosts: [],
  });
  const [newCostItem, setNewCostItem] = useState('');
  const [newCostAmount, setNewCostAmount] = useState('');
  const [saving, setSaving] = useState(false);

  // Edit state for startup costs
  const [editingCostIndex, setEditingCostIndex] = useState<number | null>(null);
  const [editCostItem, setEditCostItem] = useState('');
  const [editCostAmount, setEditCostAmount] = useState('');

  // Load data from Firebase
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) return;

      try {
        const docRef = doc(db, 'sideHustleUsers', currentUser.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.businessCanvas) {
            setFormData(data.businessCanvas);
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, [currentUser]);

  // Save data to Firebase
  const saveData = async (data: BusinessCanvasData) => {
    if (!currentUser) return;

    setSaving(true);
    try {
      const docRef = doc(db, 'sideHustleUsers', currentUser.uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        await setDoc(docRef, {
          ...docSnap.data(),
          businessCanvas: {
            ...data,
            lastUpdated: new Date(),
          },
          lastUpdated: new Date(),
        });
      } else {
        await setDoc(docRef, {
          userId: currentUser.uid,
          email: currentUser.email,
          createdAt: new Date(),
          lastUpdated: new Date(),
          businessCanvas: {
            ...data,
            lastUpdated: new Date(),
          },
        });
      }
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field: keyof BusinessCanvasData, value: string) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);
  };

  const handleBlur = () => {
    saveData(formData);
  };

  const addCostItem = () => {
    if (newCostItem.trim() && newCostAmount) {
      const updated = {
        ...formData,
        startupCosts: [
          ...formData.startupCosts,
          { item: newCostItem.trim(), amount: parseFloat(newCostAmount) },
        ],
      };
      setFormData(updated);
      setNewCostItem('');
      setNewCostAmount('');
      saveData(updated);
    }
  };

  const removeCostItem = (index: number) => {
    const updated = {
      ...formData,
      startupCosts: formData.startupCosts.filter((_, i) => i !== index),
    };
    setFormData(updated);
    saveData(updated);
  };

  // Edit cost item functions
  const startEditingCost = (index: number) => {
    setEditingCostIndex(index);
    setEditCostItem(formData.startupCosts[index].item);
    setEditCostAmount(formData.startupCosts[index].amount.toString());
  };

  const cancelEditingCost = () => {
    setEditingCostIndex(null);
    setEditCostItem('');
    setEditCostAmount('');
  };

  const saveCostEdit = () => {
    if (editingCostIndex !== null && editCostItem.trim() && editCostAmount) {
      const updatedCosts = [...formData.startupCosts];
      updatedCosts[editingCostIndex] = {
        item: editCostItem.trim(),
        amount: parseFloat(editCostAmount),
      };
      const updated = { ...formData, startupCosts: updatedCosts };
      setFormData(updated);
      saveData(updated);
      setEditingCostIndex(null);
      setEditCostItem('');
      setEditCostAmount('');
    }
  };

  const totalStartupCosts = formData.startupCosts.reduce(
    (sum, item) => sum + item.amount,
    0
  );

  return (
    <SideHustleLayout currentPage="canvas">
      <div className="flex flex-col items-center p-6 min-h-[calc(100vh-80px)]">
        <div className="max-w-5xl w-full">
          <h1 className="text-5xl font-bold text-center mb-4">Business Model Canvas</h1>
          <p className="text-xl text-center text-gray-600 mb-12">
            Let's build out your side hustle
          </p>

          <div className="space-y-6">
            {/* Problem & Solution */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-3xl shadow-lg">
                <h2 className="text-2xl font-bold mb-3">Problem</h2>
                <p className="text-gray-600 mb-4 text-sm">
                  What current problem are you working to solve?
                </p>
                <textarea
                  value={formData.problem}
                  onChange={(e) => handleFieldChange('problem', e.target.value)}
                  onBlur={handleBlur}
                  placeholder="Enter Free Text"
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>

              <div className="bg-white p-6 rounded-3xl shadow-lg">
                <h2 className="text-2xl font-bold mb-3">Solution</h2>
                <p className="text-gray-600 mb-4 text-sm">
                  How does your product or service solve problems for your user?
                </p>
                <textarea
                  value={formData.solution}
                  onChange={(e) => handleFieldChange('solution', e.target.value)}
                  onBlur={handleBlur}
                  placeholder="Enter Free Text"
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>
            </div>

            {/* Competitive Advantage & Market Competition */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-3xl shadow-lg">
                <h2 className="text-2xl font-bold mb-3">Competitive Advantage</h2>
                <p className="text-gray-600 mb-4 text-sm">
                  What makes you stand out? What are you better at than anyone else?
                </p>
                <textarea
                  value={formData.competitiveAdvantage}
                  onChange={(e) => handleFieldChange('competitiveAdvantage', e.target.value)}
                  onBlur={handleBlur}
                  placeholder="Enter Free Text"
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>

              <div className="bg-white p-6 rounded-3xl shadow-lg">
                <h2 className="text-2xl font-bold mb-3">Market & Competition</h2>
                <p className="text-gray-600 mb-4 text-sm">
                  Who else is in this space? What do they do well, where do they lack?
                </p>
                <textarea
                  value={formData.marketCompetition}
                  onChange={(e) => handleFieldChange('marketCompetition', e.target.value)}
                  onBlur={handleBlur}
                  placeholder="Enter Free Text"
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>
            </div>

            {/* Team & Ideal Customer */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-3xl shadow-lg">
                <h2 className="text-2xl font-bold mb-3">Team</h2>
                <p className="text-gray-600 mb-4 text-sm">
                  What areas do you need support? Who can help?
                </p>
                <textarea
                  value={formData.team}
                  onChange={(e) => handleFieldChange('team', e.target.value)}
                  onBlur={handleBlur}
                  placeholder="Enter Free Text"
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>

              <div className="bg-white p-6 rounded-3xl shadow-lg">
                <h2 className="text-2xl font-bold mb-3">Ideal Customer</h2>
                <p className="text-gray-600 mb-4 text-sm">
                  Who is the best person for your product or service? What traits and habits do they have?
                </p>
                <textarea
                  value={formData.idealCustomer}
                  onChange={(e) => handleFieldChange('idealCustomer', e.target.value)}
                  onBlur={handleBlur}
                  placeholder="Enter Free Text"
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>
            </div>

            {/* Startup Costs & Business Model */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-3xl shadow-lg">
                <h2 className="text-2xl font-bold mb-3">Startup Costs</h2>
                <p className="text-gray-600 mb-4 text-sm">
                  What does it cost to get your side hustle started?
                </p>

                <div className="mb-4 space-y-2">
                  <input
                    type="text"
                    value={newCostItem}
                    onChange={(e) => setNewCostItem(e.target.value)}
                    placeholder="Item name"
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={newCostAmount}
                      onChange={(e) => setNewCostAmount(e.target.value)}
                      placeholder="Amount"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <button
                      onClick={addCostItem}
                      className="bg-primary text-white px-4 py-2 rounded-xl hover:bg-primary/90 transition-all font-semibold"
                    >
                      Add
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  {formData.startupCosts.map((cost, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-secondary px-4 py-2 rounded-xl"
                    >
                      {editingCostIndex === index ? (
                        <div className="flex-1 flex gap-2 items-center">
                          <input
                            type="text"
                            value={editCostItem}
                            onChange={(e) => setEditCostItem(e.target.value)}
                            placeholder="Item"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                            autoFocus
                          />
                          <input
                            type="number"
                            value={editCostAmount}
                            onChange={(e) => setEditCostAmount(e.target.value)}
                            placeholder="Amount"
                            className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                          <button
                            onClick={saveCostEdit}
                            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 font-semibold"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEditingCost}
                            className="bg-gray-400 text-white px-4 py-2 rounded-lg hover:bg-gray-500 font-semibold"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <>
                          <span className="text-gray-800">
                            {cost.item}: ${cost.amount.toFixed(2)}
                          </span>
                          <div className="flex gap-2">
                            <button
                              onClick={() => startEditingCost(index)}
                              className="text-primary hover:text-primary/80 font-semibold"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => removeCostItem(index)}
                              className="text-red-600 hover:text-red-800 font-semibold"
                            >
                              ×
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                  {formData.startupCosts.length > 0 && (
                    <div className="border-t-2 border-gray-300 pt-2 mt-2">
                      <p className="font-bold text-lg">
                        Total: ${totalStartupCosts.toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white p-6 rounded-3xl shadow-lg">
                <h2 className="text-2xl font-bold mb-3">Business Model</h2>
                <p className="text-gray-600 mb-4 text-sm">
                  How are you going to make money?
                </p>
                <textarea
                  value={formData.businessModel}
                  onChange={(e) => handleFieldChange('businessModel', e.target.value)}
                  onBlur={handleBlur}
                  placeholder="Enter Free Text"
                  rows={10}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>
            </div>
          </div>

          {saving && (
            <div className="text-center mt-6 text-primary font-semibold">
              Saving...
            </div>
          )}
        </div>
      </div>
    </SideHustleLayout>
  );
};

export default BusinessCanvas;
